'use client'
import { CurationContext } from '@/app/(site)/edit/gifs/curation-context'
import { comscoreConfig } from '@/app/util/comscore'
import UserContext from '@/context/user-context'
import { NexaBlack } from '@/styles/fonts'
import { cn } from '@/util/cn'
import { IGif } from '@giphy/js-types'
import { Grid as SDKGrid } from '@giphy/react-components'
import { CS_SIDEBAR_WIDTH, SIDEBAR_WIDTH } from 'edit/panel/constants'
import dynamic from 'next/dynamic'
import { usePathname, useRouter } from 'next/navigation'
import {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import useWindowSize from 'react-use/lib/useWindowSize'
import styled from 'styled-components'
import { Icon } from 'ui/src/components/logo'
import { desktopWidth } from 'ui/src/constants'
import { desktop } from 'ui/src/css'
import UAParserContext from '../../context/ua-parser'
import { relativeUrl } from '../../util/url'
import useClientRender from '../../util/use-client-render'
import Avatar from '../avatar'
import SoundToggle from '../sound-toggle'
import BulkEditButton from './bulk-edit-button'
import { useBulkEditOpen } from './bulk-edit-state'
import { usePageViewTracking } from './use-page-view-tracking'
import { useAdMediaClick } from '@/util/ad-click'

const GifOverlay = dynamic(() => import('./overlay/gif-overlay'), { ssr: false })

type Props = Pick<SDKGrid['props'], 'initialGifs' | 'fetchGifs' | 'onGifClick' | 'overlay' | 'columnOffsets'> & {
    gaTrackingUrl?: (page: number) => string
    noShowMessage?: ReactNode
    width?: number
    columns?: number
    externalGifs?: IGif[]
    setExternalGifs?: Dispatch<SetStateAction<IGif[]>>
}

type GridContextProps = {
    gifs: IGif[]
    setGifs: Dispatch<SetStateAction<IGif[]>>
}

export const GridContext = createContext({
    gifs: [],
    setGifs: () => {},
} as GridContextProps)

const NoGifsMessage = styled.p`
    text-align: center;
    padding: 10px;
`

export function useExternalGifs(
    initialGifs: IGif[],
    hoistedGifs?: IGif[],
    hoistedState?: Dispatch<SetStateAction<IGif[]>>
) {
    const [_externalGifs, _setExternalGifs] = useState<IGif[]>(initialGifs)
    return [hoistedGifs || _externalGifs, hoistedState || _setExternalGifs] as const
}

const defaultMobileWindowWidth = 375
const Grid = ({
    initialGifs = [],
    fetchGifs,
    gaTrackingUrl,
    width,
    columns,
    columnOffsets,
    onGifClick,
    overlay,
    noShowMessage,
    externalGifs: _externalGifs,
    setExternalGifs: _setExternalGifs,
}: Props) => {
    const [hasGifsToShow, setHasGifsToShow] = useState(true)
    const router = useRouter()
    const pathname = usePathname()
    const { user } = useContext(UserContext)
    const curationContext = useContext(CurationContext)
    const params = curationContext?.params ?? {}
    let [externalGifs, setExternalGifs] = useExternalGifs(initialGifs, _externalGifs, _setExternalGifs)

    const [gifCount, setGifCount] = useState(0)
    usePageViewTracking(gaTrackingUrl, gifCount)
    const onAdMediaClick = useAdMediaClick()
    const { deviceType, isBot } = useContext(UAParserContext)
    let { width: windowWidth } = useWindowSize(defaultMobileWindowWidth)
    const isEditGifs = pathname.includes('/edit/gifs')
    const clientRender = useClientRender()
    const hasRendered = useRef(new Set([windowWidth]))
    const [mobileWidth, setMobileWidth] = useState(defaultMobileWindowWidth)
    useEffect(() => {
        if (hasRendered.current.size > 2) {
            setMobileWidth(windowWidth)
        }
        hasRendered.current.add(windowWidth)
    }, [windowWidth])

    const canRender = clientRender || (isBot && /^(\/explore\/|\/$)/.test(pathname))
    let isDesktop = deviceType === 'desktop'
    if (canRender) {
        isDesktop = windowWidth >= desktop.breakpointWidth
    }
    const loaderConfig = useMemo(() => (canRender ? { rootMargin: '0px 0px 250px 0px' } : undefined), [canRender])
    const eagerLoadingIds = useMemo(() => initialGifs?.map((gif) => gif.id), [initialGifs])
    const bulkEditOpen = useBulkEditOpen()
    if (!hasGifsToShow && externalGifs.length === 0) {
        return noShowMessage ? <NoGifsMessage>{noShowMessage}</NoGifsMessage> : null
    }
    const bulkEditColumns = isEditGifs ? 4 : windowWidth > 1040 ? 5 : 4

    const showSoundToggle = (['ON', 'ONLY'] as (string | undefined)[]).includes(params?.videos)

    return (
        <GridContext.Provider value={{ gifs: externalGifs, setGifs: setExternalGifs }}>
            {isEditGifs && (
                <div className="mb-3 flex w-full items-center justify-between">
                    <div className="z-20 flex items-center" style={{ minHeight: 50 }}>
                        <Icon />
                        <h1
                            className={`ml-2 text-xl font-light text-giphyWhite sm:text-2xl lg:text-3xl ${NexaBlack.className}`}
                        >
                            Curation Station
                        </h1>
                    </div>
                    <div className={`flex items-center gap-3 ${bulkEditOpen ? 'mr-[400px]' : ''}`}>
                        {user && (
                            <a
                                href={user.channel}
                                className="flex min-h-[22px] cursor-pointer items-center gap-1 px-2 py-1 text-[13px] leading-[18px]"
                            >
                                <div className="flex-shrink-0">
                                    <Avatar user={user} size={20} />
                                </div>
                                <div className="overflow-hidden text-ellipsis whitespace-nowrap text-white select-none">
                                    {user.display_name || user.username}
                                </div>
                            </a>
                        )}
                        {clientRender && showSoundToggle ? (
                            <div className="w-[50px]">
                                <SoundToggle />
                            </div>
                        ) : null}
                    </div>
                </div>
            )}
            <BulkEditButton />
            <div
                className={cn(
                    bulkEditOpen && isEditGifs
                        ? 'no-scrollbar fixed inset-0 z-modal mt-19 flex justify-center overflow-y-auto bg-giphyBlack'
                        : bulkEditOpen &&
                              'no-scrollbar fixed inset-0 z-modal flex justify-center overflow-y-auto bg-giphyBlack'
                )}
                style={
                    bulkEditOpen
                        ? isEditGifs
                            ? { marginRight: SIDEBAR_WIDTH, marginLeft: CS_SIDEBAR_WIDTH }
                            : bulkEditOpen
                              ? { marginRight: SIDEBAR_WIDTH }
                              : {}
                        : {}
                }
            >
                <SDKGrid
                    columns={bulkEditOpen ? bulkEditColumns : columns || (isDesktop ? 4 : 2)}
                    width={
                        bulkEditOpen
                            ? isEditGifs
                                ? Math.min(windowWidth - (SIDEBAR_WIDTH + CS_SIDEBAR_WIDTH), 1440) - 12 * 2
                                : Math.min(windowWidth - SIDEBAR_WIDTH, 1440) - 12 * 2
                            : width || (isDesktop ? desktopWidth : mobileWidth)
                    }
                    percentWidth={isDesktop ? undefined : '100%'}
                    fetchGifs={async (offset) => {
                        const result = await fetchGifs(offset)
                        if (window.COMSCORE?.beacon) {
                            window.COMSCORE.beacon(comscoreConfig)
                            fetch('/comscore-page-view')
                        }
                        if (result.data.length === 0 && initialGifs?.length === 0) {
                            setHasGifsToShow(false)
                        }
                        return result
                    }}
                    externalGifs={externalGifs}
                    initialGifs={initialGifs}
                    eagerIds={eagerLoadingIds}
                    onGifSeen={(gif) => {
                        if (gif.decision?.impression_tracking_url) {
                            console.log('fire tracking url', gif.decision.impression_tracking_url)
                            fetch(gif.decision.impression_tracking_url, { mode: 'no-cors', keepalive: true })
                        }
                    }}
                    onGifClick={(gif, event) => {
                        if (!onGifClick) {
                            event.preventDefault()
                            event.stopPropagation()
                            if (gif.decision?.click_tracking_url) {
                                onAdMediaClick({
                                    clickTrackingUrl: gif.decision.click_tracking_url,
                                    clickUrl: gif.decision.click_url,
                                    gifUrl: gif.url,
                                })
                            } else {
                                router.push(relativeUrl(gif.url))
                            }
                        } else {
                            onGifClick(gif, event)
                        }
                    }}
                    gutter={isDesktop ? 12 : undefined}
                    overlay={overlay || (isDesktop ? GifOverlay : undefined)}
                    hideAttribution
                    loaderConfig={loaderConfig}
                    onGifsFetched={(gifs) => {
                        setGifCount(gifs.length)
                        setExternalGifs(gifs)
                    }}
                    columnOffsets={columnOffsets}
                />
            </div>
        </GridContext.Provider>
    )
}

export default Grid
